import type { Configuration } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { AUTH_TYPES, type AuthType } from '../models/auth.model';

import MsalManager from './MsalManager';

type MsalContextProviderProps = PropsWithChildren<{
  authType: AuthType;
  config: Configuration;
}>;

type MsalContextType = {
  authType: AuthType;
};
const MsalContext = createContext<MsalContextType>({
  authType: AUTH_TYPES.CDI,
});

export const MsalContextProvider = ({
  children,
  authType,
  config,
}: MsalContextProviderProps): JSX.Element => {
  const managerRef = useRef<MsalManager | null>(null);
  const [ready, setReady] = useState(false);

  if (!managerRef.current) {
    managerRef.current = new MsalManager(authType, config);
  }

  useEffect(() => {
    const manager = managerRef.current;
    if (!manager) {
      return;
    }

    const setupManager = async () => {
      try {
        await manager.ready;
      } catch (error) {
        console.error('Failed to initialize MSAL manager.', error);
      } finally {
        setReady(true);
      }
    };

    void setupManager();

    return () => {
      manager.dispose();
      managerRef.current = null;
      setReady(false);
    };
  }, []);

  if (!ready) {
    return <></>;
  }

  return (
    <MsalContext.Provider value={{ authType }}>
      <MsalProvider instance={managerRef.current.instance}>
        {children}
      </MsalProvider>
    </MsalContext.Provider>
  );
};

export const useMsalContext = () => {
  return useContext(MsalContext);
};
